import { ComponentType } from '@angular/cdk/portal';
import { Injectable, TemplateRef } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material';
import { isNull, isUndefined } from 'lodash-es';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { blank, data_get, filled, Omit, OptionalValue } from '../shared/utils/common';
import { Observable, of } from 'rxjs';
import { NotificationService } from './notification.service';
import { DataStateStore } from '../shared/store/data-state.store';

export { MAT_DIALOG_DATA as DIALOG_DATA } from '@angular/material';

@Injectable()
export class DialogService {
  constructor(
    private readonly builder: MatDialog,
    private readonly _notifications: NotificationService,
    private readonly _states: DataStateStore,
  ) { }

  show<C, D = any, R = any>(props: ShowDialogProps<C, D, R>): void {
    props = Object.assign({
      dismissable: true,
      custom_settings: {},
    }, props);

    const dialog = this.builder.open<C, D, R>(
      props.component,
      {
        data: props.data,
        panelClass: this._computeDialogSize(props.size),
        disableClose: ! props.dismissable,
        closeOnNavigation: ! props.dismissable,
        minWidth: this._computeWidth(props.size),
        maxWidth: this._computeWidth(props.size),
        ... props.custom_settings,
      },
    );

    if (!isUndefined(props.beforeClosing)) {
      dialog.beforeClosed()
        .pipe(take(1))
        .subscribe(() => props.beforeClosing());
    }

    if (!isUndefined(props.afterClosing)) {
      dialog.afterClosed()
        .pipe(take(1))
        .subscribe((result) => props.afterClosing(result));
    }
  }

  show$ = <C, D = any, R = any>(props: ShowDialogProps<C, D, R>) => new Observable<R>((observer) => this.show({
    ... props,
    afterClosing: (result) => {
      observer.next(result);
      observer.complete();
    }
  }));

  close<I, R = any>(props: CloseDialogProps<I, R>): void {
    const matches = this.builder.openDialogs.filter((ref) => ref.componentInstance === props.instance);

    if (matches.length < 1) {
      return;
    }

    const dialog = matches[0];

    if (isUndefined(dialog) || isNull(dialog)) {
      return;
    }

    this._dismiss(of(null), {
      dialog,
      result: props.result,
      data_state_key: data_get(props.dismissal, 'data_state_key'),
      predicate$: data_get(props.dismissal, 'predicate$'),
    });
  }

  confirmBeforeDismiss<I>(props: FieldmagicConfirmDismissalProps): void {
    const matches = this.builder.openDialogs.filter((ref) => ref.componentInstance === props.instance);

    if (blank(matches)) {
      return;
    }

    const dialog = matches[0];

    if (blank(dialog)) {
      return;
    }

    /// disable closing as we would manually do that
    dialog.disableClose = true;

    this._listenToBackdrop(dialog, {
      predicate$: props.predicate$,
      data_state_key: props.data_state_key,
    });
  }

  private _computeDialogSize(size: DialogSize = 'md'): string {
    switch (size) {
      case 'full':
        return 'w-100';
      case 'xl':
        return 'w-90';
      case 'lg':
        return 'w-75';
      case 'sm':
        return 'w-30';
      case 'xs':
        return 'w-20';
      case 'xxs':
        return 'w-10';
      case 'md':
      default:
        return 'w-50';
    }
  }

  private _computeWidth(size: DialogSize = 'md'): string {
    switch (size) {
      case 'full':
        return '100%';
      case 'xl':
        return '90%';
      case 'lg':
        return '75%';
      case 'sm':
        return '30%';
      case 'xs':
        return '20%';
      case 'xxs':
        return '10%';
      case 'md':
      default:
        return '50%';
    }
  }

  private _listenToBackdrop = (dialog: MatDialogRef<any>, opts: {
    predicate$?: FieldmagicConfirmDismissalProps<any>['predicate$'];
    data_state_key?: FieldmagicConfirmDismissalProps<any>['data_state_key'];
  } = {}) => this._dismiss(dialog.backdropClick(), {
    dialog,
    ... opts,
  });

  private _dismiss = (init$: Observable<any>, opts: {
    dialog: MatDialogRef<any>
    predicate$?: FieldmagicConfirmDismissalProps<any>['predicate$'];
    data_state_key?: FieldmagicConfirmDismissalProps<any>['data_state_key'];
    result?: any;
  }) => init$.pipe(
    switchMap(() => {
      if (filled(opts.predicate$)) {
        return opts.predicate$();
      }

      if (filled(opts.data_state_key)) {
        return of(this._states.has(opts.data_state_key));
      }

      return of(false);
    }),
    switchMap((requiresConfirmation) => {
      if (! requiresConfirmation) {
        return of(true);
      }

      return this._notifications.sendConfirmation('confirm_cancel').pipe(
        map((confirmation) => confirmation.answer),
      );
    }),
    filter((confirmed) => confirmed),
    tap(() => {
      if (blank(opts.data_state_key)) {
        return;
      }

      this._states.destroy(opts.data_state_key);
    })
  ).subscribe(() => opts.dialog.close(opts.result));
}

type IDialogComponent<C> = ComponentType<C> | TemplateRef<C>;

type ShowDialogProps<C, D = any, R = any> = {
  /**
   * The dialog component that will be shown to the user.
   */
  component: IDialogComponent<C>;
  /**
   * The data that will be injected to the component when rendering.
   * Useful to pass data to the rendering component.
   */
  data?: D
  /**
   * The size of the dialog to be displayed. Defaults to `md`
   */
  size?: DialogSize;
  /**
   * A flag that dictates that the rendered dialog can be closed using escape or clicking
   * outside of the dialog. Default: true,
   */
  dismissable?: boolean;
  /**
   * A hook that will be called before a dialog is closed
   */
  beforeClosing?: () => void;
  /**
   * A hook that will be called when dialog is closed. If a result is provided
   * from the closing dialog it will be passed down to this hook.
   */
  afterClosing?: (result: OptionalValue<R>) => void;

  /// override settings directly
  custom_settings?: MatDialogConfig;
}

type CloseDialogProps<I, R = any> = {
  /**
   * The instance of the dialog to be closed
   */
  instance: I;
  /**
   * The result to be emitted for listeners of the dialog when closed
   */
  result?: R;

  dismissal?: Omit<FieldmagicConfirmDismissalProps<I>, 'instance'>;
}

export type DialogSize = 'full' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs' | 'xl';

export type FieldmagicConfirmDismissalProps<I = any> = {
  instance: I;

  /// a function called that will be used to verify if the dialog is eligable to be closed
  predicate$?: () => Observable<boolean>;

  /// alternative to closing predicate. this would be used to verify if the dialog contains unsaved state
  /// and will automatically notify the client of unsaved state
  data_state_key?: string;
}
